// ./src/pages/private/EventCompliancePage/AttachPolicyDrawer.tsx
//
// Picker to attach one or more account-level policies to an event. Lists the
// account's policies that aren't already attached; the author multi-selects and
// attaches. Attaching creates a REFERENCE (event_policy row) — the policy text
// stays on the account policy. A new policy can be created in place (a stacked
// PolicyEditDrawer) and is auto-attached on save.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Group, Text, Checkbox, Badge, Button, Box, ThemeIcon, ScrollArea, Skeleton } from "@mantine/core";
import { StackedDrawer } from "@em/ui";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { usePolicies } from "@/hooks";
import { EventPolicyService, Policy, WHOLE_SET } from "@/utils/api";
import { useToast } from "@/hooks";
import { AppIcon, EmptyState, ErrorAlert } from "@/components/generic";
import { PolicyEditDrawer } from "@/components/features";
import { refusalReason } from "@/utils";
import { policyAcceptanceSummary } from "@/utils/complianceSetup";
import { FEATURE_CODES } from "@/config";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_FOR_EVENT_SETTINGS } from "@/utils/createInPlace";

export interface AttachPolicyDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** policyIds already attached to the event (hidden from the picker). */
  attachedPolicyIds: Set<string>;
  /** Highest existing orderNo, so new attachments append. */
  maxOrderNo: number;
  onAttached: () => void;
}

export const AttachPolicyDrawer: React.FC<AttachPolicyDrawerProps> = ({
  opened,
  onClose,
  eventId,
  attachedPolicyIds,
  maxOrderNo,
  onAttached,
}) => {
  const { t } = useTranslation(["events", "common"]);
  const toast = useToast();

  // Only ACTIVE account policies can be attached. eventId is the auth-context so a cross-account crew
  // member (co_host) sees the event account's policy library instead of an empty picker.
  // ⓘ This hook instance only READS, so its `lastError` is the list read's own failure.
  // ⓘ A picker shows EVERY policy — read whole, never a capped page (ROADMAP #118).
  const { policies, loading, isLoaded, lastError: readError } = usePolicies({
    pageSize: WHOLE_SET,
    initialFilters: { status: [STATUSES.ACTIVE] },
    eventId,
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    if (opened) {
      setSelected(new Set());
    }
  }, [opened]);

  const available = useMemo(
    () => policies.filter((p) => p.id && !attachedPolicyIds.has(p.id)),
    [policies, attachedPolicyIds]
  );

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleAttach = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      return;
    }

    setAttaching(true);

    // Append in the picker order, spacing order numbers by 1000.
    let order = maxOrderNo;
    let failures = 0;

    for (const policyId of ids) {
      order += 1000;
      const result = await EventPolicyService.attach({ eventId, policyId, isRequired: false, orderNo: order });
      if (result.code !== OP_RESULT_CODES.OK) {
        failures += 1;
      }
    }

    setAttaching(false);

    if (failures === 0) {
      toast.success(
        t("events:compliance.attach.done", { defaultValue: "Attached {{count}} policy(ies).", count: ids.length })
      );
    } else {
      toast.error(
        t("events:compliance.attach.partial", {
          defaultValue: "{{failed}} of {{total}} could not be attached.",
          failed: failures,
          total: ids.length,
        })
      );
    }

    onAttached();
    onClose();
  }, [selected, maxOrderNo, eventId, onAttached, onClose, toast, t]);

  const [createOpen, setCreateOpen] = useState(false);
  const openCreate = useCallback(() => setCreateOpen(true), []);
  // #142 — a policy made here belongs to the EVENT's account: its plan (pro registration) decides, and a crew member
  // needs `event.settings` (PolicyCore's rule).
  const canCreatePolicy = useCreateInPlace(CREATE_FOR_EVENT_SETTINGS, FEATURE_CODES.PRO_REGISTRATION, eventId);

  // A policy created in place is attached to this event immediately.
  const handleCreatedAndAttach = useCallback(
    async (policy: Policy) => {
      setCreateOpen(false);

      if (!policy.id) {
        return;
      }

      const result = await EventPolicyService.attach({
        eventId,
        policyId: policy.id,
        isRequired: false,
        orderNo: maxOrderNo + 1000,
      });

      if (result.code === OP_RESULT_CODES.OK) {
        toast.success(t("events:compliance.attach.createdAttached", { defaultValue: "Policy created and attached." }));
        onAttached();
        onClose();
      } else {
        toast.error(refusalReason(result) || t("events:compliance.attach.attachFailed", { defaultValue: "Could not attach the new policy." }));
      }
    },
    [eventId, maxOrderNo, onAttached, onClose, toast, t]
  );

  const requirementText = useCallback(
    (p: Policy): string => {
      const parts: string[] = [];
      // #164 — a signature / initials exist only on the signing page, so a tick-box policy never asks for them.
      const how = policyAcceptanceSummary(p);
      if (how.hasText) {
        parts.push(
          how.byLink
            ? t("events:compliance.attach.byLink", { defaultValue: "a link to accept" })
            : t("events:compliance.attach.tickBox", { defaultValue: "a tick box" }),
        );
      }
      if (how.signature) parts.push(t("events:compliance.attach.signature", { defaultValue: "signature" }));
      if (how.initials) parts.push(t("events:compliance.attach.initials", { defaultValue: "initials" }));
      if (p.minAge != null || p.maxAge != null) parts.push(t("events:compliance.attach.ageBound", { defaultValue: "age-limited" }));
      return parts.join(" · ");
    },
    [t]
  );

  const renderBody = () => {
    if (loading && !isLoaded) {
      return (
        <Stack gap="sm">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={56} radius="sm" />
          ))}
        </Stack>
      );
    }

    // ⚠ A failed read is not "No policies yet" — that tells the host their library is gone.
    if (readError) {
      return <ErrorAlert error={readError} title={t("events:compliance.attach.loadFailed", { defaultValue: "Could not load your policies" })} page="Compliance" operation="policyList" />;
    }

    if (policies.length === 0) {
      return (
        <EmptyState
          iconName="Gavel"
          title={t("events:compliance.attach.noneTitle", { defaultValue: "No policies yet" })}
          description={t("events:compliance.attach.noneDesc", {
            defaultValue: "Create a reusable waiver or consent in account settings, then attach it here.",
          })}
          actionLabel={canCreatePolicy ? t("events:compliance.attach.createNew", { defaultValue: "Create a policy" }) : undefined}
          actionIcon="Plus"
          onAction={canCreatePolicy ? openCreate : undefined}
          size="md"
        />
      );
    }

    if (available.length === 0) {
      return (
        <Stack gap="sm" align="center" py="lg">
          <ThemeIcon variant="light" color="teal" size={44} radius="xl">
            <AppIcon iconName="Check" size={22} />
          </ThemeIcon>
          <Text size="sm" c="dimmed" ta="center">
            {t("events:compliance.attach.allAttached", {
              defaultValue: "All your policies are already attached to this event.",
            })}
          </Text>
          {canCreatePolicy ? (
            <Button variant="light" size="md" leftSection={<AppIcon iconName="Plus" size={16} />} onClick={openCreate}>
              {t("events:compliance.attach.createNew", { defaultValue: "Create a policy" })}
            </Button>
          ) : null}
        </Stack>
      );
    }

    return (
      <ScrollArea.Autosize mah="calc(100vh - 220px)">
        <Stack gap="xs">
          {available.map((p) => {
            const reqs = requirementText(p);
            const isSel = !!p.id && selected.has(p.id);

            return (
              <Box
                key={p.id}
                onClick={() => p.id && toggle(p.id)}
                style={{
                  cursor: "pointer",
                  border: `1px solid ${isSel ? "var(--mantine-color-brand-6)" : "var(--mantine-color-default-border)"}`,
                  background: isSel ? "var(--mantine-color-brand-light)" : undefined,
                  borderRadius: "var(--mantine-radius-md)",
                  padding: "var(--mantine-spacing-sm)",
                }}
              >
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  <Checkbox checked={isSel} onChange={() => p.id && toggle(p.id)} tabIndex={-1} mt={2} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={6} wrap="nowrap">
                      <Text fw={600} size="md" lineClamp={1}>
                        {p.name || t("events:compliance.untitled", { defaultValue: "Untitled policy" })}
                      </Text>
                      <Badge size="xs" variant="light" color={p.policyText ? "grape" : "blue"}>
                        {p.policyText
                          ? t("events:compliance.document", { defaultValue: "Document" })
                          : t("events:compliance.checkbox", { defaultValue: "Checkbox" })}
                      </Badge>
                    </Group>
                    {p.label && (
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {p.label}
                      </Text>
                    )}
                    {reqs && (
                      <Text size="xs" c="dimmed" mt={2}>
                        {t("events:compliance.attach.requires", { defaultValue: "Requires" })} {reqs}
                      </Text>
                    )}
                  </Box>
                </Group>
              </Box>
            );
          })}
        </Stack>
      </ScrollArea.Autosize>
    );
  };

  return (
    <>
      <StackedDrawer
        opened={opened}
        onClose={onClose}
        position="right"
        size="md"
        title={
          <Text fw={600}>{t("events:compliance.attach.title", { defaultValue: "Attach policies" })}</Text>
        }
      >
        <Stack gap="md" style={{ height: "100%" }}>
          <Text size="sm" c="dimmed">
            {t("events:compliance.attach.subtitle", {
              defaultValue: "Attach reusable account policies to this event. Editing a policy updates it everywhere it's used.",
            })}
          </Text>

          {canCreatePolicy && available.length > 0 && (
            <Button
              variant="light"
              fullWidth
              leftSection={<AppIcon iconName="Plus" size={16} />}
              onClick={openCreate}
              disabled={attaching}
            >
              {t("events:compliance.attach.createNew", { defaultValue: "Create a new policy" })}
            </Button>
          )}

          {renderBody()}

          {available.length > 0 && (
            <Group justify="flex-end" mt="auto">
              <Button variant="default" onClick={onClose} disabled={attaching}>
                {t("common:buttons.cancel", { defaultValue: "Cancel" })}
              </Button>
              <Button
                onClick={handleAttach}
                loading={attaching}
                disabled={selected.size === 0}
                leftSection={<AppIcon iconName="Plus" size={16} />}
              >
                {t("events:compliance.attach.attachN", {
                  defaultValue: "Attach {{count}}",
                  count: selected.size,
                })}
              </Button>
            </Group>
          )}
        </Stack>
      </StackedDrawer>

      {/* Create a new account policy in place → auto-attach on save. */}
      <PolicyEditDrawer
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        recordId={null}
        onCreated={handleCreatedAndAttach}
      />
    </>
  );
};
